/**
 * Deciding what has to move between sessions, and in which direction.
 *
 * This is the whole of the sharing logic that is worth reasoning about, and it is kept
 * pure so that it can be: no network, no storage, no clock. Everything above it —
 * WebRTC, IndexedDB, the progress bar — is plumbing that carries out the plan this
 * produces.
 *
 * A model is identified by the SHA-256 of its bytes, never by its name. Two files with
 * the same name are two different models if their contents differ, and the same file
 * saved under two names is one model. The consequence worth knowing is the other way
 * round: the same object exported twice by a slicer is very unlikely to be byte-identical,
 * so it counts as two models.
 *
 * ## How a deletion beats an addition, and when it does not
 *
 * Each session holds, for a given hash, one of three states: it has the model, it has a
 * tombstone saying the model was deleted, or it has never heard of it. Merging those is
 * last-writer-wins on wall-clock time — the most recent event about a hash decides.
 *
 * Wall-clock is a real weakness and is chosen knowingly. The sessions being merged are
 * different machines, their clocks are only as aligned as whatever set them, and there is
 * no server ordering to fall back on because the server is deliberately never told that
 * any of these models exist. A device whose clock is an hour slow can have its deletion
 * overruled by an older addition elsewhere. The alternative — per-session version vectors
 * — is a great deal more machinery for a feature whose job is copying files between a
 * laptop and a headset, and it would still need a tie-break rule of its own.
 *
 * Exact ties resolve in favour of *keeping* the model. Both mistakes are possible, and
 * they are not equally bad: a deletion wrongly lost leaves a file to delete again, while
 * an addition wrongly lost deletes that file from every session at once.
 *
 * ## The file and the decisions about it are reconciled separately
 *
 * A file never changes; that is what identifying it by a hash means. How it is *shown*
 * does — its colour, and how large it stands in the room — and those are the only things
 * two sessions can hold the same model and still disagree about.
 *
 * So there are two answers per model rather than one. Which sessions should hold the file
 * is settled by the addition and deletion times above. How it should look is settled field
 * by field by its own times, folded across every session rather than taken from whichever
 * of them happens to have added the file earliest — the session that last recoloured a
 * model is very often not the one that has held it longest.
 *
 * That produces work for a session that already holds a model and would otherwise have
 * been left alone: an `Amendment`, carrying no bytes. Without it a colour or a size would
 * only ever reach sessions receiving the file for the first time, which is where this
 * began.
 */

/** Lowercase hex SHA-256 of the file's bytes. */
export type ModelHash = string;

/** Identifies one participating session for the duration of a share. */
export type PeerId = string;

/**
 * How a model is to be shown, and when each of those decisions was made.
 *
 * Separate from the file, because it is the one part of a model that changes after it has
 * been added. The bytes never do — that is what a hash identifies — so a model is either
 * present or absent everywhere and there is nothing to reconcile beyond which. A colour or
 * a size is different: two sessions can hold the same file and disagree about how it should
 * look, and both of them are entitled to have an opinion.
 *
 * Each decision carries its own time rather than the record carrying one. Otherwise a
 * session that recoloured a model would overwrite a size somebody else had set on it a
 * moment earlier, purely by being the more recent write of the two — and neither change
 * has anything to do with the other. Per field, each one survives on its own merits.
 *
 * A time is absent for a decision made before this was recorded, which compares as older
 * than anything that does carry one. That is the right way round: a session that can say
 * when it changed something has better evidence than one that cannot.
 */
export interface ModelPresentation {
  readonly colour?: string | undefined;
  /** Epoch milliseconds at which that colour was chosen. */
  readonly colourAt?: number | undefined;
  /** How large to show it in the room, as a multiple of true size. */
  readonly roomScale?: number | undefined;
  /** Epoch milliseconds at which that size was settled on. */
  readonly roomScaleAt?: number | undefined;
}

/** One model, as described to another session. Never includes the bytes. */
export interface ManifestEntry extends ModelPresentation {
  readonly hash: ModelHash;
  readonly name: string;
  readonly byteSize: number;
  /**
   * Epoch milliseconds at which this model entered the account's library.
   *
   * Deliberately not "when this browser received it": a copy that arrives by sharing
   * keeps the timestamp of the original addition, or a later re-addition would look newer
   * than a deletion it actually predates and would undo it.
   */
  readonly addedAt: number;
}

/**
 * A change to how an existing model is shown, for a session that already holds the file.
 *
 * Carries the whole merged presentation rather than only the fields that have moved, so
 * that applying one is idempotent and a receiver that has since learned something newer
 * still keeps it — the timestamps are compared again at the far end, where they have to be
 * anyway. It is a few bytes either way, and there are no bytes of file involved at all.
 */
export interface Amendment extends ModelPresentation {
  readonly hash: ModelHash;
}

/** A record that a model was deliberately removed, kept so the removal can travel. */
export interface Tombstone {
  readonly hash: ModelHash;
  /** Epoch milliseconds at which the deletion happened. */
  readonly deletedAt: number;
}

/** Everything one session knows, and the only thing it sends before any bytes move. */
export interface Manifest {
  readonly models: readonly ManifestEntry[];
  readonly tombstones: readonly Tombstone[];
}

export const EMPTY_MANIFEST: Manifest = { models: [], tombstones: [] };

/** The merged truth across every participating session. */
export interface MergedState {
  /** Hashes that should exist everywhere, described by their most recent addition. */
  readonly present: ReadonlyMap<ModelHash, ManifestEntry>;
  /** Hashes that should exist nowhere, with the deletion that settled it. */
  readonly deleted: ReadonlyMap<ModelHash, Tombstone>;
}

/**
 * What one session has to do to match the merged truth.
 *
 * `tombstones` is not only the deletions that hit something. A session that never had a
 * model still records the tombstone, because otherwise the next share would see a peer
 * that holds the model, a session that has never heard of it, and no evidence of the
 * deletion — and would helpfully put it back.
 */
export interface SessionPlan {
  /** Models this session lacks and must be sent. */
  readonly wanted: readonly ManifestEntry[];
  /**
   * Models it holds, but is showing in a way somebody has since changed.
   *
   * Only where another session can prove its decision is the later one. Two sessions that
   * disagree with no evidence between them are left disagreeing, because there is nothing
   * to say which of them is behind.
   */
  readonly amend: readonly Amendment[];
  /** Deletions this session has not yet recorded. */
  readonly tombstones: readonly Tombstone[];
}

/** The presentation decisions in something that carries them, and nothing else. */
export function presentationOf(source: ModelPresentation): ModelPresentation {
  return {
    ...(source.colour === undefined ? {} : { colour: source.colour }),
    ...(source.colourAt === undefined ? {} : { colourAt: source.colourAt }),
    ...(source.roomScale === undefined ? {} : { roomScale: source.roomScale }),
    ...(source.roomScaleAt === undefined ? {} : { roomScaleAt: source.roomScaleAt }),
  };
}

/**
 * Fold two sessions' decisions about one model together, field by field.
 *
 * A value is only displaced by another value, never by an absence: there is no way to
 * un-choose a colour locally, so an entry that simply does not mention one is a session
 * that has nothing to say rather than one asking for the colour to be taken away.
 *
 * Ties keep what is already there. Two sessions that stamped a change in the same
 * millisecond have to be settled somehow, and settling it in favour of the incumbent at
 * least makes the fold order-independent for the case that matters — the same pair of
 * manifests merged on two machines reaches the same answer.
 */
export function mergePresentation(
  held: ModelPresentation | undefined,
  incoming: ModelPresentation,
): ModelPresentation {
  const mine = held ?? {};

  const colour = later(
    { value: mine.colour, at: mine.colourAt },
    { value: incoming.colour, at: incoming.colourAt },
  );
  const roomScale = later(
    { value: mine.roomScale, at: mine.roomScaleAt },
    { value: incoming.roomScale, at: incoming.roomScaleAt },
  );

  return {
    ...(colour.value === undefined ? {} : { colour: colour.value }),
    ...(colour.at === undefined ? {} : { colourAt: colour.at }),
    ...(roomScale.value === undefined ? {} : { roomScale: roomScale.value }),
    ...(roomScale.at === undefined ? {} : { roomScaleAt: roomScale.at }),
  };
}

/** One decision, with the time it was taken. */
interface Decision<T> {
  readonly value: T | undefined;
  readonly at: number | undefined;
}

/** Whichever of two decisions should stand. See the rules on `mergePresentation`. */
function later<T>(held: Decision<T>, incoming: Decision<T>): Decision<T> {
  if (incoming.value === undefined) return held;
  if (held.value === undefined) return incoming;
  return newer(held.at, incoming.at) ? incoming : held;
}

/**
 * Whether a session holding `held` is behind what everyone together has settled on.
 *
 * Strictly behind: equal times mean nothing has moved, and this is what decides whether a
 * peer is told anything at all, so an "or equal" here would have every share send an
 * amendment for every model that has ever been recoloured.
 */
export function isBehind(held: ModelPresentation, merged: ModelPresentation): boolean {
  return (
    newer(held.colourAt, merged.colourAt) || newer(held.roomScaleAt, merged.roomScaleAt)
  );
}

/** True when `candidate` is later than `existing`, an absent time counting as long ago. */
function newer(existing: number | undefined, candidate: number | undefined): boolean {
  return (candidate ?? 0) > (existing ?? 0);
}

/**
 * The initiator's plan for a whole share.
 *
 * One session presses the button and acts as the hub: it collects every manifest, works
 * out the union, pulls what it lacks, and only then pushes to each peer. Doing it in that
 * order is what makes a single press converge *all* the sessions rather than just the one
 * that started it — a peer receives not only what the initiator already had, but whatever
 * the initiator has just learned from the other peers.
 */
export interface SharePlan {
  /** What to fetch, grouped by the peer chosen to supply it. */
  readonly pull: ReadonlyMap<PeerId, readonly ManifestEntry[]>;
  /** What to send each peer once everything has been pulled. */
  readonly push: ReadonlyMap<PeerId, readonly ManifestEntry[]>;
  /** What the initiator must delete or record locally. */
  readonly local: SessionPlan;
  /** What each peer must delete or record. */
  readonly tombstonesFor: ReadonlyMap<PeerId, readonly Tombstone[]>;
  /** How each peer must change the look of models it already holds. */
  readonly amendFor: ReadonlyMap<PeerId, readonly Amendment[]>;
}

/**
 * Fold every session's view of the library into one.
 *
 * Order of the manifests does not affect the result: the winner for each hash is decided
 * by timestamp, not by position.
 */
export function merge(manifests: readonly Manifest[]): MergedState {
  const newestAdd = new Map<ModelHash, ManifestEntry>();
  const newestDelete = new Map<ModelHash, Tombstone>();

  /**
   * How each model is to be shown, folded across every session rather than taken from
   * whichever of them added the file last.
   *
   * The two questions are unrelated. Which session added a file first decides what a
   * deletion is weighed against; who last chose its colour decides what colour it is, and
   * the session that did so may well not be the one that has held the file longest.
   */
  const looks = new Map<ModelHash, ModelPresentation>();

  for (const manifest of manifests) {
    for (const entry of manifest.models) {
      const known = newestAdd.get(entry.hash);
      if (!known || entry.addedAt > known.addedAt) newestAdd.set(entry.hash, entry);
      looks.set(entry.hash, mergePresentation(looks.get(entry.hash), entry));
    }
    for (const stone of manifest.tombstones) {
      const known = newestDelete.get(stone.hash);
      if (!known || stone.deletedAt > known.deletedAt)
        newestDelete.set(stone.hash, stone);
    }
  }

  const present = new Map<ModelHash, ManifestEntry>();
  const deleted = new Map<ModelHash, Tombstone>();

  for (const [hash, entry] of newestAdd) {
    const stone = newestDelete.get(hash);
    // Strictly greater, so an exact tie keeps the model. See the note at the top.
    if (stone && stone.deletedAt > entry.addedAt) {
      deleted.set(hash, stone);
    } else {
      // The file as the oldest addition describes it, wearing whatever everyone between
      // them has since settled on.
      present.set(hash, { ...entry, ...looks.get(hash) });
    }
  }

  // Deletions of models nobody still holds. These have to survive the merge, or a session
  // that has the tombstone and a session that has neither would agree on nothing, and a
  // third session still holding the model would win by default at the next share.
  for (const [hash, stone] of newestDelete) {
    if (!newestAdd.has(hash)) deleted.set(hash, stone);
  }

  return { present, deleted };
}

/** What a session holding `manifest` must acquire and record to match `merged`. */
export function planFor(manifest: Manifest, merged: MergedState): SessionPlan {
  const held = new Map(manifest.models.map((entry) => [entry.hash, entry] as const));
  const recorded = new Map(
    manifest.tombstones.map((stone) => [stone.hash, stone.deletedAt] as const),
  );

  const wanted: ManifestEntry[] = [];
  const amend: Amendment[] = [];

  for (const [hash, entry] of merged.present) {
    const mine = held.get(hash);

    // A model it does not have arrives carrying the merged colour and size already, so
    // there is never both a transfer and an amendment for the same model.
    if (!mine) {
      wanted.push(entry);
      continue;
    }

    if (isBehind(mine, entry)) amend.push({ hash, ...presentationOf(entry) });
  }

  const tombstones: Tombstone[] = [];
  for (const [hash, stone] of merged.deleted) {
    const already = recorded.get(hash);
    // A tombstone it already holds at the same time or later teaches it nothing new to
    // record. One it holds with an *older* time is worth replacing, so that its next
    // manifest reports the deletion that actually won.
    const worthRecording = already === undefined || already < stone.deletedAt;
    // Sent regardless if the session still holds the model, because applying a tombstone
    // is also what removes the file — and a session can hold both, having re-added a
    // model and then been outvoted by a later deletion elsewhere.
    if (worthRecording || held.has(hash)) tombstones.push(stone);
  }

  return { wanted, amend, tombstones };
}

/**
 * Work out the whole share from the initiator's point of view.
 *
 * Each wanted model is pulled from exactly one peer, chosen as the first in the given
 * order that holds it. Which one hardly matters — the bytes are identical by definition,
 * that being what the hash means — but picking one rather than all of them is the
 * difference between fetching a 40 MB model once and fetching it once per session.
 */
export function planShare(
  local: Manifest,
  peers: ReadonlyMap<PeerId, Manifest>,
): SharePlan {
  const merged = merge([local, ...peers.values()]);
  const localPlan = planFor(local, merged);

  const pull = new Map<PeerId, ManifestEntry[]>();
  const push = new Map<PeerId, readonly ManifestEntry[]>();
  const tombstonesFor = new Map<PeerId, readonly Tombstone[]>();
  const amendFor = new Map<PeerId, readonly Amendment[]>();

  const holders = new Map<PeerId, Set<ModelHash>>();
  for (const [peerId, manifest] of peers) {
    holders.set(peerId, new Set(manifest.models.map((entry) => entry.hash)));
  }

  for (const entry of localPlan.wanted) {
    for (const [peerId, hashes] of holders) {
      if (!hashes.has(entry.hash)) continue;
      const batch = pull.get(peerId);
      if (batch) batch.push(entry);
      else pull.set(peerId, [entry]);
      break;
    }
  }

  for (const [peerId, manifest] of peers) {
    const plan = planFor(manifest, merged);
    if (plan.wanted.length > 0) push.set(peerId, plan.wanted);
    if (plan.amend.length > 0) amendFor.set(peerId, plan.amend);
    if (plan.tombstones.length > 0) tombstonesFor.set(peerId, plan.tombstones);
  }

  return { pull, push, local: localPlan, tombstonesFor, amendFor };
}

/**
 * Total bytes a plan will move over the network, for showing progress against.
 *
 * Counts each pull once and each push once, because that is what actually crosses a
 * connection — a model pulled from one peer and pushed to two others is three transfers
 * of it, not one.
 */
export function plannedBytes(plan: SharePlan): number {
  let total = 0;
  for (const batch of plan.pull.values()) {
    for (const entry of batch) total += entry.byteSize;
  }
  for (const batch of plan.push.values()) {
    for (const entry of batch) total += entry.byteSize;
  }
  return total;
}
